// src/components/family/profile/ChildProfileLayout.tsx
// Main layout wrapper for child profile pages

import React, { useState } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { ChildProfileHeader, ChildProfileHeaderSkeleton } from './ChildProfileHeader';
import { ChildProfileTabs, ChildProfileTabsVertical } from './ChildProfileTabs';
import { useChildProfile } from '@/features/children/hooks/useChildProfile';
import { EditChildModal } from '@/features/children/components/EditChildModal';

interface ChildProfileLayoutProps {
  children?: React.ReactNode;
}

export const ChildProfileLayout: React.FC<ChildProfileLayoutProps> = ({
  children,
}) => {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: profile, isLoading, error } = useChildProfile(childId);

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: 'Children', href: '/family/children' },
    { 
      label: profile 
        ? `${profile.nickname || profile.firstName} ${profile.lastName}`
        : 'Loading...',
    },
  ];

  // Compute badge counts from profile data
  const tabBadges = profile
    ? {
        appointments: profile.upcomingAppointments?.length || 0,
        medications: profile.currentMedications?.length || 0,
        goals: profile.stats?.activeGoalsCount || 0,
      }
    : {};

  const handleBack = () => {
    navigate('/family/children');
  };

  const handleEditChild = () => {
    setIsEditModalOpen(true);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-soft max-w-md mx-4">
          <div className="w-16 h-16 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">😢</span>
          </div>
          <h2 className="text-xl font-display font-semibold text-navy mb-2">
            Child Not Found
          </h2>
          <p className="text-warm-gray mb-6">
            We couldn't find this child's profile. They may have been removed or you may not have access.
          </p>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg font-medium hover:bg-sage-dark transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Children
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Sticky header area */}
      <div className="sticky top-0 z-30 bg-cream/95 backdrop-blur-sm border-b border-cream-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs row */}
          <div className="py-3 flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 -ml-2 rounded-lg text-warm-gray hover:text-navy hover:bg-cream-dark transition-colors lg:hidden"
              aria-label="Back to children"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <Breadcrumbs items={breadcrumbItems} className="flex-1" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Profile header */}
        <div className="mb-6">
          {isLoading ? (
            <ChildProfileHeaderSkeleton />
          ) : profile ? (
            <ChildProfileHeader
              child={profile}
              primaryDiagnosis={profile.primaryDiagnosis}
              onEdit={handleEditChild}
            />
          ) : null}
        </div>

        {/* Two-column layout on large screens */}
        <div className="lg:grid lg:grid-cols-[260px,1fr] lg:gap-8">
          {/* Sidebar with vertical tabs - desktop only */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              {childId && (
                <ChildProfileTabsVertical childId={childId} badges={tabBadges} />
              )}
              
              {/* Quick stats sidebar card */}
              {profile && (
                <div className="mt-6 p-4 bg-white rounded-xl border border-cream-dark">
                  <h4 className="text-sm font-semibold text-navy mb-3">
                    Quick Info
                  </h4>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-warm-gray">Age</dt>
                      <dd className="font-medium text-navy">
                        {calculateAge(profile.dateOfBirth)}
                      </dd>
                    </div>
                    {profile.bloodType && (
                      <div className="flex justify-between">
                        <dt className="text-warm-gray">Blood Type</dt>
                        <dd className="font-medium text-navy">
                          {profile.bloodType}
                        </dd>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <dt className="text-warm-gray">Diagnoses</dt>
                      <dd className="font-medium text-navy">
                        {profile.diagnoses?.length || 0}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-warm-gray">Care Team</dt>
                      <dd className="font-medium text-navy">
                        {profile.careTeam?.length || 0} members
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>
          </aside>

          {/* Main content area */}
          <main className="min-w-0">
            {/* Horizontal tabs - mobile/tablet */}
            <div className="lg:hidden mb-6 -mx-4 px-4 py-2 bg-white border-y border-cream-dark">
              {childId && (
                <ChildProfileTabs childId={childId} badges={tabBadges} />
              )}
            </div>

            {/* Tab content */}
            <div className="space-y-6">
              {children || <Outlet context={{ profile, isLoading }} />}
            </div>
          </main>
        </div>
      </div>

      {/* Edit modal */}
      {profile && (
        <EditChildModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          child={profile}
        />
      )}
    </div>
  );
};

// Helper function to calculate age
function calculateAge(dateOfBirth: string): string {
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  const years = today.getFullYear() - birthDate.getFullYear();
  const months = today.getMonth() - birthDate.getMonth();
  
  if (years === 0) {
    const totalMonths = months < 0 ? 12 + months : months;
    return `${totalMonths} month${totalMonths !== 1 ? 's' : ''}`;
  }
  
  const adjustedMonths = months < 0 ? 12 + months : months;
  const adjustedYears = months < 0 ? years - 1 : years;
  
  if (adjustedYears === 0) {
    return `${adjustedMonths} month${adjustedMonths !== 1 ? 's' : ''}`;
  }
  
  return `${adjustedYears}y ${adjustedMonths}m`;
}

// Context type for child routes
export interface ChildProfileContext {
  profile: ReturnType<typeof useChildProfile>['data'];
  isLoading: boolean;
}

export default ChildProfileLayout;
